import { COUNTRY_ORDER, REGIONS } from '@constants/i18n'
import type { CountryCodes, COUNTRY_CODES } from '@constants/countryDetails'
import type { AppLanguages } from '@constants/i18n'

const lowerCase = (string = '') => string.toLowerCase()
const upperCase = (string = '') => string.toUpperCase()

type GetCanonicalUrlOptions = {
  country?: CountryCodes
  countryCodes?: COUNTRY_CODES[]
  language: AppLanguages
  slug: string
  baseUrl: string
  experiment?: {
    isVariant?: boolean | null
    controlPageSlug?: string | null
  }
}

type ConstructUrlOptions = Pick<
  GetCanonicalUrlOptions,
  'language' | 'slug' | 'baseUrl'
> & {
  canonical: CountryCodes | ''
}

type GetBestCanonical = CountryCodes[]

type GetCanonicalOptions = {
  countryCodes?: CountryCodes[]
  defaultCanonical?: CountryCodes | null
  isNativeLanguagePage?: boolean
}

const constructUrl = ({
  canonical = '',
  language,
  slug,
  baseUrl,
}: ConstructUrlOptions): string => {
  const separator = language && canonical ? '-' : ''
  const locale = language + separator + canonical.toLowerCase()
  const slugWithoutFirstSlash = slug.replace(/^\//g, '')

  return [baseUrl, locale, slugWithoutFirstSlash].filter(Boolean).join('/')
}

const getBestCanonical = (countryCodes: GetBestCanonical = []) => {
  return COUNTRY_ORDER.find(code => countryCodes.map(upperCase).includes(code))
}

const getCanonical = (options: GetCanonicalOptions) => {
  const { countryCodes, defaultCanonical, isNativeLanguagePage } = options

  const fallbackCanonical = getBestCanonical(
    countryCodes
  )?.toLowerCase() as CountryCodes

  const canonical = isNativeLanguagePage
    ? defaultCanonical || fallbackCanonical
    : fallbackCanonical

  return canonical || ''
}

const getCanonicalUrl = (options: GetCanonicalUrlOptions) => {
  const experimentData = options?.experiment
  const baseUrl = options?.baseUrl
  const language = options.language.toLowerCase() as AppLanguages
  // We don’t always have country.
  const country = (options?.country ?? '').toLowerCase() as CountryCodes
  const countryCodes = (options?.countryCodes ?? []).map(
    lowerCase
  ) as CountryCodes[]
  const slug = options?.slug

  // If the page is an experiment variant, the canonical is the contol url
  if (experimentData?.isVariant && experimentData.controlPageSlug) {
    return constructUrl({
      canonical: country,
      language,
      slug: experimentData.controlPageSlug,
      baseUrl,
    })
  }

  const selfCanonical = constructUrl({
    canonical: country,
    language,
    slug,
    baseUrl,
  })

  // Let’s just return early if there’s no country, so we don’t need to worry
  // about it later on.
  if (!country) return selfCanonical

  // If the page only has one country-version (or some cases none), then that
  // one version must be the canonical.
  if (countryCodes.length < 2) return selfCanonical

  // Check if `de-de`, `fr-fr` or such.
  const isNativeLanguagePage = REGIONS[country].defaultLanguage === language

  // If we’re dealing with a page `de-de/foo` the canonical is not `de-eu/foo`,
  // but a self-canonical.
  if (isNativeLanguagePage) return selfCanonical

  // Let’s make the canonical from the data we have.
  const canonical = getCanonical({
    countryCodes,
    isNativeLanguagePage,
  })

  return constructUrl({ canonical, language, slug, baseUrl })
}

export { getCanonical, getBestCanonical, getCanonicalUrl }
